import { useCallback, useRef, useState } from 'react';

import { applyManualFieldErrors } from '#/modules/commonComponents/form/applyManualFieldErrors';

function defaultFormatSummaryMessage(issue) {
  return `${issue.path}：${issue.message}`;
}

/**
 * 表單驗證摘要 state（submit 嘗試、自訂訊息），供 FormValidationSummary 搭配使用。
 */
export function useFormValidationSummary() {
  const [customMessages, setCustomMessages] = useState([]);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const lastManualPathsRef = useRef([]);

  const clearCustomMessages = useCallback(function clearCustomMessages() {
    setCustomMessages([]);
  }, []);

  const resetSubmitAttempted = useCallback(function resetSubmitAttempted() {
    setIsSubmitAttempted(false);
  }, []);

  const markSubmitAttempted = useCallback(function markSubmitAttempted() {
    setIsSubmitAttempted(true);
  }, []);

  const resetValidationSummary = useCallback(function resetValidationSummary() {
    setCustomMessages([]);
    setIsSubmitAttempted(false);
    lastManualPathsRef.current = [];
  }, []);

  /**
   * 套用自訂驗證 issues：標記已送出、清上一輪 manual、setError 紅框、寫入 summary。
   *
   * @param {Function} setError
   * @param {Function} clearErrors
   * @param {{ path: string, message: string }[]} issues
   * @param {object} [options]
   * @param {(issue: { path: string, message: string }) => string} [options.formatSummaryMessage]
   * @param {string[]} [options.pathsToClear] - 覆寫要清的路徑；預設上一輪 manual paths
   */
  const applyManualIssues = useCallback(function applyManualIssues(
    setError,
    clearErrors,
    issues,
    options = {},
  ) {
    const list = Array.isArray(issues) ? issues : [];
    const formatSummaryMessage = options.formatSummaryMessage ?? defaultFormatSummaryMessage;
    const pathsToClear = options.pathsToClear ?? lastManualPathsRef.current;

    setIsSubmitAttempted(true);

    applyManualFieldErrors(setError, list, {
      clearErrors,
      pathsToClear: Array.isArray(pathsToClear) ? pathsToClear : [],
    });

    lastManualPathsRef.current = list
      .map((issue) => issue?.path)
      .filter(Boolean);

    setCustomMessages(list.map((issue) => formatSummaryMessage(issue)));
  }, []);

  /**
   * 清除上一輪 applyManualIssues 寫入的 summary 與 RHF manual errors。
   * @param {Function} [clearErrors]
   */
  const clearManualIssues = useCallback(function clearManualIssues(clearErrors) {
    if (typeof clearErrors === 'function' && lastManualPathsRef.current.length > 0) {
      clearErrors(lastManualPathsRef.current);
    }
    lastManualPathsRef.current = [];
    setCustomMessages([]);
  }, []);

  return {
    customMessages,
    setCustomMessages,
    clearCustomMessages,
    isSubmitAttempted,
    setIsSubmitAttempted,
    markSubmitAttempted,
    resetSubmitAttempted,
    resetValidationSummary,
    applyManualIssues,
    clearManualIssues,
  };
}

export default useFormValidationSummary;
